import React, { useEffect, useRef } from 'react';
import { StrapiMediaInfo } from '../apolloClient/types';
import {
  getMediumImageUrlFromImage,
  getOriginalImageUrlFromImage,
} from 'lib/getImages';

export type PopUpGalleryProps = {
  openPopupLarge?: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      initialShowedIndex: number;
    }>
  >;
  images: StrapiMediaInfo[];
};

const PopUpGallery: React.FC<PopUpGalleryProps> = ({
  images,
  openPopupLarge,
}) => {
  useEffect(() => {
    const preventZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const handleGestureStart = (e: Event) => {
      e.preventDefault();
    };

    const handleGestureChange = (e: Event) => {
      e.preventDefault();
    };

    const handleGestureEnd = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', preventTouchStart, {
      passive: false,
    });
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('gesturestart', handleGestureStart);
    document.addEventListener('gesturechange', handleGestureChange);
    document.addEventListener('gestureend', handleGestureEnd);

    return () => {
      document.removeEventListener('touchstart', preventTouchStart);
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('gesturestart', handleGestureStart);
      document.removeEventListener('gesturechange', handleGestureChange);
      document.removeEventListener('gestureend', handleGestureEnd);
    };
  }, []);

  // Tap guard: a touch that moved, scrolled, or stopped momentum scroll must not open a photo
  const TAP_MOVE_THRESHOLD = 10;
  const MOMENTUM_WINDOW_MS = 150;
  const CLICK_AFTER_TOUCH_MS = 800;
  const lastScrollAtRef = useRef(0);
  const touchRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    cancelled: false,
    endedAt: 0,
  });

  useEffect(() => {
    // scroll doesn't bubble; capture catches the dialog's scroll container
    const handleScroll = () => {
      lastScrollAtRef.current = Date.now();
      if (touchRef.current.active) touchRef.current.cancelled = true;
    };
    document.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    });
    return () =>
      document.removeEventListener('scroll', handleScroll, { capture: true });
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      cancelled:
        e.touches.length > 1 ||
        Date.now() - lastScrollAtRef.current < MOMENTUM_WINDOW_MS,
      endedAt: 0,
    };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const t = touchRef.current;
    const touch = e.touches[0];
    if (
      e.touches.length > 1 ||
      Math.abs(touch.clientX - t.startX) > TAP_MOVE_THRESHOLD ||
      Math.abs(touch.clientY - t.startY) > TAP_MOVE_THRESHOLD
    ) {
      t.cancelled = true;
    }
  };

  const handleTouchEnd = () => {
    touchRef.current.active = false;
    touchRef.current.endedAt = Date.now();
  };

  const isClickFromCancelledTouch = () => {
    const t = touchRef.current;
    return t.cancelled && Date.now() - t.endedAt < CLICK_AFTER_TOUCH_MS;
  };

  // Chunk images into groups of 5
  const imageGroups = [];
  for (let i = 0; i < images.length; i += 5) {
    imageGroups.push(images.slice(i, i + 5));
  }

  const handleImageClick = (index: number) => {
    if (isClickFromCancelledTouch()) return;
    if (openPopupLarge) {
      openPopupLarge({ isOpen: true, initialShowedIndex: index });
    }
  };

  return (
    <div
      className="container-1280-no-paddings md:px-[30px] lg:px-[40px] h-full flex-1 min-h-0"
      style={{ touchAction: 'pan-y' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {imageGroups.map((group, groupIndex) => {
        const baseIndex = groupIndex * 5;
        return (
          <div key={groupIndex} className="mb-4">
            {/* Single column on mobile, two columns on larger screens for first 4 images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {group.slice(0, 4).map((image, idx) => (
                <div key={image.hash} className="relative w-full pt-[56.25%]">
                  <img
                    className="absolute top-0 left-0 w-full h-full object-cover cursor-pointer select-none"
                    style={{ userSelect: 'none' }}
                    src={getMediumImageUrlFromImage(image)}
                    alt={image.name}
                    onClick={() => handleImageClick(baseIndex + idx)}
                  />
                </div>
              ))}
            </div>
            {/* Full width image */}
            {group[4] && (
              <div className="relative w-full pt-[56.25%]">
                <img
                  className="absolute top-0 left-0 w-full h-full object-cover cursor-pointer select-none"
                  style={{ userSelect: 'none' }}
                  src={getOriginalImageUrlFromImage(group[4])}
                  alt={group[4].name}
                  onClick={() => handleImageClick(baseIndex + 4)}
                />
              </div>
            )}
          </div>
        );
      })}
      {/* Phone: keeps the last photo 20px above the sticky Request Info bar */}
      <div className="h-[56px] md:hidden" />
    </div>
  );
};

export default PopUpGallery;
